import { useMemo } from 'react';

import {
  ChartTimeSeriesLine,
  ChartTimeSeriesLineProps,
  ChartTimeSeriesLineTheme,
  LINE_COLOR,
} from '@hcs/design-system';
import { useScreenSize } from '@hcs/hooks';
import { calcMobileMarginAdjustment, getD3NumberFormat } from '@hcs/utils';

import { CerberusStatsHpiTS } from '../../types';

const FORECAST_HPI_DATA_ID = 'forecastHpi';
const LEGEND_ITEMS: ChartTimeSeriesLineProps['legendItems'] = [
  {
    id: 'historical',
    title: 'Historical HPI',
    background: { type: 'single', color: LINE_COLOR },
  },
  {
    id: 'forecast',
    title: 'Forecasted HPI',
    background: { type: 'single', color: LINE_COLOR },
    isDotDashed: true,
  },
];

const D3_Y_FORMAT = getD3NumberFormat({ precision: 1 });

export interface CerberusStatsHpiChartProps {
  dataHcName: string;
  className?: string;
  isLoading: boolean;
  data: CerberusStatsHpiTS | null | undefined;
  theme?: Partial<ChartTimeSeriesLineTheme>;
}
export const CerberusStatsHpiChart = ({
  className,
  dataHcName,
  isLoading,
  data,
  theme,
}: CerberusStatsHpiChartProps) => {
  const { isPhoneWidth } = useScreenSize();
  const historicalTimeSeries = data?.historicalTimeSeries;
  const forecastTimeSeries = data?.forecastTimeSeries;
  const chartData: ChartTimeSeriesLineProps['chartData'] = useMemo(() => {
    if (!historicalTimeSeries || !forecastTimeSeries) {
      return null;
    }
    const historicalLastRecord =
      historicalTimeSeries[historicalTimeSeries.length - 1];
    // const historicalLastValue = historicalLastRecord?.value;
    if (historicalLastRecord == null) {
      return null;
    }
    const forecastTimeSeriesWithLastHistoric = [
      historicalLastRecord,
      ...forecastTimeSeries,
    ];
    return [
      {
        id: 'historicalHpi',
        data:
          historicalTimeSeries?.map((record) => {
            return {
              x: `${record.month}`,
              y: record.valueIndexed,
            };
          }) || [],
      },
      {
        id: FORECAST_HPI_DATA_ID,
        data:
          forecastTimeSeriesWithLastHistoric?.map((record) => {
            return {
              x: `${record.month}`,
              y: record.valueIndexed,
            };
          }) || [],
      },
    ];
  }, [historicalTimeSeries, forecastTimeSeries]);

  const mergedTheme = {
    Container: className,
    ...theme,
  };

  const mobileMargin =
    isPhoneWidth && chartData?.[0].data
      ? calcMobileMarginAdjustment(chartData?.[0].data, D3_Y_FORMAT)
      : undefined;
  return (
    <ChartTimeSeriesLine
      dataHcName={dataHcName}
      isLoading={isLoading}
      chartData={chartData}
      title="Home Price Index"
      rightAxisMargin={mobileMargin}
      tooltipDateFormat={'monthYear'}
      d3FormatterForY={D3_Y_FORMAT}
      tickRotationOverride={isPhoneWidth ? -60 : undefined}
      theme={mergedTheme}
      forecastSerieId={FORECAST_HPI_DATA_ID}
      legendItems={LEGEND_ITEMS}
      compactTitle={isPhoneWidth}
      compactValue={isPhoneWidth}
    />
  );
};
